import { useCallback, useEffect, useState } from 'react';
import {
  COMPANY_FORM_INITIAL_STATE,
  COMPANY_TYPE,
  ENTERPRISE_COMPANY_TYPE,
  GENERAL_COMPANY_TYPE,
  handleApiErrors,
  useToast,
} from '@consolidocs-ui/utils';
import { CompanyType } from '@consolidocs-ui/types';
import {
  useCreateCompanyMutation,
  useRetryProvisioningMutation,
  useUpdateCompanyMutation,
} from '@consolidocs-ui/data-access/CompanyApi';
import {
  buildOrganizationPayload,
  extractOrganizationId,
  validateStep1,
  validateStep2,
} from './utils';
import { useEnterpriseOrgStatus } from '@consolidocs-ui/hooks/useEnterpriseOrgStatus';

type Errors = Partial<Record<keyof CompanyType, string>>;

/**
 * Beat between the last resource turning green and leaving the wizard, so the
 * completed list is actually visible instead of flashing past.
 */
const PROVISION_DONE_DELAY_MS = 1200;

// Fields that live on Step 1 — used to bounce back there when the server
// rejects one of them.
const STEP1_FIELDS: (keyof CompanyType)[] = [
  'name',
  'email',
  'description',
  'address1',
  'city',
  'state',
  'postalCode',
  'country',
  'companyType',
  'industry',
  'contract_types',
];

interface UseCompanyFormParams {
  editData?: CompanyType;
  onSuccess?: () => void;
}

// The type dropdown is read-only in edit, so coerce an empty or unrecognised
// value to a real option — otherwise the required check blocks saving.
const normalizeCompanyType = (value?: string): string => {
  const normalized = (value || '').toLowerCase();
  return COMPANY_TYPE.some((option) => option.value === normalized)
    ? normalized
    : GENERAL_COMPANY_TYPE;
};

/**
 * All state and behaviour behind the organization wizard: form data, the
 * create/update mutations, enterprise provisioning polling, and the step
 * transitions. The component only wires the returned values to the UI.
 */
export const useCompanyForm = ({ editData, onSuccess }: UseCompanyFormParams) => {
  const [formData, setFormData] = useState<CompanyType>(
    COMPANY_FORM_INITIAL_STATE as CompanyType
  );
  const [errors, setErrors] = useState<Errors>({});
  const [activeStep, setActiveStep] = useState<0 | 1>(0);
  // Set once an enterprise organization has been created: the wizard then
  // stays on Step 2 and watches its infrastructure come up.
  const [createdOrgId, setCreatedOrgId] = useState<string>();
  const [triggerToast] = useToast();

  const [createCompany, { isLoading: isCreateLoading }] =
    useCreateCompanyMutation();
  const [updateCompany, { isLoading: isUpdateLoading }] =
    useUpdateCompanyMutation();
  const [retryProvisioningMutation, { isLoading: isRetryingProvision }] =
    useRetryProvisioningMutation();
  const isSubmitting = isCreateLoading || isUpdateLoading;

  const isEdit = !!editData;
  const isEnterprise = formData.companyType === ENTERPRISE_COMPANY_TYPE;
  // Editing a general org is a single step (its Step 2 is only a review);
  // enterprise keeps Step 2 to show its infrastructure status.
  const isGeneralEdit = isEdit && !isEnterprise;

  // Watch the org we just created, or the one being edited. General
  // organizations have no infrastructure, so nothing is polled for them.
  const {
    status: provisionStatus,
    flags: polledFlags,
    isComplete: isProvisioned,
    isPolling: isProvisioning,
    isTimedOut: isProvisionTimedOut,
    isFailed: isProvisionFailed,
    error: provisionStatusError,
    restart: restartProvisionWatch,
  } = useEnterpriseOrgStatus(createdOrgId || editData?.id, {
    enabled: isEnterprise,
  });

  // Until the org shows up on the status endpoint, fall back to whatever the
  // organization record itself reports.
  const provisionFlags = provisionStatus
    ? polledFlags
    : {
        isDbCreated: !!formData.isDbCreated,
        isCognitoPoolCreated: !!formData.isCognitoPoolCreated,
        isSqsReady: !!formData.isSqsReady,
        isServiceReady: !!formData.isServiceReady,
      };

  const handleChange = useCallback(
    (field: keyof CompanyType, value: unknown) => {
      setFormData((prev) => ({ ...prev, [field]: value } as CompanyType));
      setErrors((prev) => ({ ...prev, [field]: '' }));
    },
    []
  );

  const reportError = (err: any, fallback: string) => {
    console.error(fallback, err);
    handleApiErrors<CompanyType>(
      err,
      setErrors as any,
      (message: string, color: 'success' | 'danger') =>
        triggerToast({ message, type: color }),
      err?.data?.data?.error || fallback
    );
    const hasStep1Error = STEP1_FIELDS.some(
      (f) => (err?.data?.errors as any)?.[f] || (err?.data?.[f] as any)
    );
    if (hasStep1Error) setActiveStep(0);
  };

  const submit = async () => {
    try {
      const payload = buildOrganizationPayload(formData, { isEdit });

      if (editData) {
        await updateCompany({ ...payload, id: editData.id }).unwrap();
      } else {
        const created = await createCompany(payload).unwrap();
        const newOrgId = extractOrganizationId(created);

        // An enterprise organization is only half-done at this point: its
        // infrastructure is provisioned asynchronously, so hold the wizard on
        // Step 2 and let the status poll drive the rest of the flow.
        if (isEnterprise && newOrgId) {
          setCreatedOrgId(newOrgId);
          setActiveStep(1);
          triggerToast({
            message: 'Organization created — setting up infrastructure',
            type: 'success',
          });
          return;
        }
      }

      triggerToast({
        message: `Organization ${editData ? 'updated' : 'created'} successfully`,
        type: 'success',
      });
      onSuccess?.();
    } catch (err: any) {
      reportError(err, 'Failed to save organization');
    }
  };

  // Start a fresh infrastructure build for a failed organization, then resume
  // watching it so the resource list updates as the new build progresses.
  const retryProvisioning = async () => {
    const orgId = createdOrgId || editData?.id;
    if (!orgId) return;
    try {
      await retryProvisioningMutation(orgId).unwrap();
      triggerToast({ message: 'Provisioning restarted', type: 'success' });
      await restartProvisionWatch();
    } catch (err: any) {
      console.error('Failed to restart provisioning', err);
      triggerToast({
        message: err?.data?.status_message || 'Failed to restart provisioning',
        type: 'danger',
      });
    }
  };

  const goToNextStep = () => {
    if (validateStep1(formData, setErrors)) setActiveStep(1);
  };

  const goToPreviousStep = () => setActiveStep(0);

  // Stepper header click: Step 1 is always reachable; Step 2 needs a valid
  // Step 1. Locked once the org exists (nothing left to edit).
  const goToStep = (step: 0 | 1) => {
    if (createdOrgId) return;
    if (step === 0) {
      setActiveStep(0);
      return;
    }
    if (validateStep1(formData, setErrors)) setActiveStep(1);
  };

  const handleSubmit = async () => {
    if (!validateStep1(formData, setErrors)) {
      setActiveStep(0);
      return;
    }
    // Step 2 only gates creation; in edit its fields are read-only, so there
    // is nothing to validate before saving.
    if (!editData && isEnterprise && !validateStep2(formData, setErrors)) {
      setActiveStep(1);
      return;
    }
    await submit();
  };

  // Infrastructure for a freshly created organization finished coming up —
  // announce it and hand control back to the page, which returns to the list.
  useEffect(() => {
    if (!createdOrgId || !isProvisioned) return;

    triggerToast({ message: 'Organization is ready to use', type: 'success' });
    const timer = setTimeout(() => onSuccess?.(), PROVISION_DONE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [createdOrgId, isProvisioned]);

  // Hydrate the form from `editData` on open / when it changes.
  useEffect(() => {
    if (!editData) return;
    setFormData({
      ...(COMPANY_FORM_INITIAL_STATE as CompanyType),
      ...editData,
      companyType: normalizeCompanyType(editData.companyType),
      industry: editData.industry_display?.id || editData.industry || '',
      contract_types:
        editData.contract_types_display?.map((c) => c.id) ||
        editData.contract_types ||
        [],
    });
    setActiveStep(0);
    setErrors({});
  }, [editData]);

  return {
    formData,
    errors,
    activeStep,
    isEdit,
    isEnterprise,
    isGeneralEdit,
    isSubmitting,
    createdOrgId,
    isProvisioning,
    isProvisionTimedOut,
    isProvisionFailed,
    isRetryingProvision,
    provisionStatus,
    provisionFlags,
    provisionStatusError,
    retryProvisioning,
    handleChange,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    handleSubmit,
    onDone: () => onSuccess?.(),
  };
};

export type CompanyFormApi = ReturnType<typeof useCompanyForm>;
